"use client";

import { createContext, useContext, useEffect, useRef, useState } from "react";

interface TopNavHeadroomCondition {
  key: string;
  proposition: boolean;
}

interface TopNavHeadroomContext {
  isCollapsed: boolean;
  registerCondition: (condition: TopNavHeadroomCondition) => void;
}

const topNavHeadroomContext = createContext<TopNavHeadroomContext | undefined>({
  isCollapsed: false,
  registerCondition: () => null,
});

const SCROLL_THRESHOLD = 5;
const SCROLL_TOP_MARGIN = 100;

export function TopNavHeadroomProvider({
  children,
}: Readonly<{ children: React.ReactNode }>) {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [conditionsRegister, setConditionsRegister] = useState<{
    [key: string]: boolean;
  }>({});
  const lastScrollPosition = useRef(0);

  const contextValue: TopNavHeadroomContext = {
    isCollapsed: isCollapsed,
    registerCondition: (condition: TopNavHeadroomCondition) => {
      const existingProposition = conditionsRegister[condition.key];
      if (existingProposition === condition.proposition) {
        return;
      }
      const newConditions = {
        ...conditionsRegister,
        [condition.key]: condition.proposition,
      };
      setConditionsRegister(newConditions);
    },
  };

  useEffect(() => {
    const handleScroll = () => {
      if (
        Math.abs(window.scrollY - lastScrollPosition.current) > SCROLL_THRESHOLD
      ) {
        const couldCollapse =
          window.scrollY > lastScrollPosition.current &&
          window.scrollY > SCROLL_TOP_MARGIN;

        const allConditionsPass =
          Object.values(conditionsRegister).every(Boolean);

        setIsCollapsed(couldCollapse && allConditionsPass);

        lastScrollPosition.current = window.scrollY;
      }
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [conditionsRegister]);

  return (
    <topNavHeadroomContext.Provider value={contextValue}>
      {children}
    </topNavHeadroomContext.Provider>
  );
}

export function useTopNavHeadroom() {
  const context = useContext(topNavHeadroomContext);
  if (!context) {
    throw new Error(
      "useTopNavHeadroom must be used within a TopNavHeadroomProvider"
    );
  }
  return context;
}
